import type { Task } from "../types";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useGetIdentity } from "ra-core";
import { getCalendarTasks } from "./calendarData";

export function useCalendarTasks(
  filter: Record<string, unknown>,
  retainRange = false,
) {
  const provider = useDataProvider(),
    { identity } = useGetIdentity();
  return useQuery<{ data: Task[]; total: number }, Error, Task[]>({
    queryKey: [
      "tasks",
      "getList",
      {
        calendar: true,
        user: identity?.id,
        admin: identity?.administrator,
        role: identity?.role,
        filter,
      },
    ],
    enabled: !!identity,
    staleTime: retainRange ? 30_000 : 0,
    gcTime: retainRange ? 60_000 : 300_000,
    placeholderData: retainRange
      ? (previous, query) => {
          const prior = query?.queryKey[2] as
            | {
                user?: unknown;
                admin?: unknown;
                role?: unknown;
                filter?: Record<string, unknown>;
              }
            | undefined;
          if (
            !prior ||
            prior.user !== identity?.id ||
            prior.admin !== identity?.administrator ||
            prior.role !== identity?.role
          )
            return undefined;
          const scope = (value: Record<string, unknown>) =>
            JSON.stringify(
              Object.entries(value).filter(
                ([key]) =>
                  !key.startsWith("scheduled_start@") &&
                  !key.startsWith("scheduled_end@") &&
                  !key.startsWith("installation_start@") &&
                  !key.startsWith("installation_end@"),
              ),
            );
          return scope(prior.filter ?? {}) === scope(filter)
            ? previous
            : undefined;
        }
      : undefined,
    queryFn: async ({ signal }) => {
      const data = await getCalendarTasks(provider, filter, signal);
      return { data, total: data.length };
    },
    select: (result) => result.data,
  });
}
